import { useCallback, useEffect, useState } from 'react';
import { api, fmtMoney, fmtValueRange, fmtDate, type Opportunity } from '../lib/api';

const TYPE_LABELS: Record<string, string> = {
  government_procurement: 'Government Procurement',
  state_procurement: 'State/Local Procurement',
  commercial: 'Commercial',
  residential: 'Residential',
  property_manager: 'Property Manager',
  direct_request: 'Direct Request',
  other: 'Other',
};

export function MoneyRadar({ onOpenAccount }: { onOpenAccount: (id: string) => void }) {
  const [metrics, setMetrics] = useState<{ activeOpportunities: number; hotOpportunities: number; knownPotentialValue: number } | null>(null);
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [flash, setFlash] = useState<Record<string, string>>({});

  const [filters, setFilters] = useState({
    status: 'active', band: '', type: '', customerType: '', source: '', minValue: '', sinceDays: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { status: filters.status };
      if (filters.band) params.band = filters.band;
      if (filters.type) params.type = filters.type;
      if (filters.customerType) params.customerType = filters.customerType;
      if (filters.source) params.source = filters.source;
      if (filters.minValue) params.minValue = filters.minValue;
      if (filters.sinceDays) params.sinceDays = filters.sinceDays;
      const [m, o] = await Promise.all([api.metrics(), api.opportunities(params)]);
      setMetrics(m);
      setOpps(o.opportunities);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  async function doAction(opp: Opportunity, action: string, label: string) {
    setBusy((b) => ({ ...b, [opp.id]: action }));
    try {
      await api.action(opp.id, action);
      setFlash((f) => ({ ...f, [opp.id]: label }));
      if (action === 'not_for_me' || action === 'hide') {
        setOpps((rows) => rows.filter((r) => r.id !== opp.id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy((b) => ({ ...b, [opp.id]: '' }));
    }
  }

  async function doResearchProperty(opp: Opportunity) {
    setBusy((b) => ({ ...b, [opp.id]: 'research_property' }));
    try {
      const res = await api.researchProperty(opp.id);
      if (res.found && res.propertyIntelligence) {
        const p = res.propertyIntelligence as Record<string, unknown>;
        alert(
          `PUBLIC PROPERTY INTELLIGENCE\n\nTMS: ${p.tms}\nOwner: ${p.owner}\nAddress: ${p.propertyAddress ?? '—'}\nLand use: ${p.landUse ?? '—'}\nAcres: ${p.acres ?? '—'}\nAdjusted total: ${p.adjustedTotal != null ? fmtMoney(Number(p.adjustedTotal)) : '—'}\nSale date: ${p.saleDate ?? '—'}\n\n${res.notice ?? ''}`,
        );
      } else {
        alert(res.message ?? 'No parcel found.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Property research failed');
    } finally {
      setBusy((b) => ({ ...b, [opp.id]: '' }));
    }
  }

  async function doResearchAccount(opp: Opportunity) {
    setBusy((b) => ({ ...b, [opp.id]: 'research_account' }));
    try {
      const res = await api.researchAccount(opp.id);
      if (res.status === 'ok' && res.accountId) {
        onOpenAccount(res.accountId);
      } else {
        alert(res.message);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Account research failed');
    } finally {
      setBusy((b) => ({ ...b, [opp.id]: '' }));
    }
  }

  async function doCrmPush(opp: Opportunity) {
    setBusy((b) => ({ ...b, [opp.id]: 'crm' }));
    try {
      const res = await api.crmPush(opp.id);
      setFlash((f) => ({ ...f, [opp.id]: res.alreadyPushed ? 'Already in CRM' : 'Sent to CRM' }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'CRM push failed');
    } finally {
      setBusy((b) => ({ ...b, [opp.id]: '' }));
    }
  }

  return (
    <div>
      <div className="page-title">WHERE CAN I MAKE MONEY TODAY?</div>
      <div className="page-sub">Today's Money Radar — legitimate, scored roofing opportunities in your territory.</div>

      {metrics && (
        <div className="metrics">
          <div className="metric">
            <div className="label">Active Opportunities</div>
            <div className="value teal">{metrics.activeOpportunities}</div>
          </div>
          <div className="metric">
            <div className="label">Hot Opportunities</div>
            <div className="value hot">{metrics.hotOpportunities}</div>
          </div>
          <div className="metric">
            <div className="label">Known Potential Value</div>
            <div className="value">{fmtMoney(metrics.knownPotentialValue)}</div>
          </div>
        </div>
      )}

      <div className="filters">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="active">Active</option>
          <option value="saved">Saved</option>
          <option value="contacted">Contacted</option>
          <option value="won">Won</option>
          <option value="expired">Expired</option>
          <option value="all">All</option>
        </select>
        <select value={filters.band} onChange={(e) => setFilters({ ...filters, band: e.target.value })}>
          <option value="">Any score</option>
          <option value="HOT">HOT (90+)</option>
          <option value="HIGH">HIGH (75–89)</option>
          <option value="MEDIUM">MEDIUM (55–74)</option>
          <option value="LOW">LOW (&lt;55)</option>
        </select>
        <select value={filters.customerType} onChange={(e) => setFilters({ ...filters, customerType: e.target.value })}>
          <option value="">Any customer</option>
          <option value="government">Government</option>
          <option value="commercial">Commercial</option>
          <option value="residential">Residential</option>
          <option value="property_manager">Property Manager</option>
        </select>
        <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
          <option value="">Any source</option>
          <option value="sam_gov">SAM.gov</option>
          <option value="scbo">SCBO</option>
        </select>
        <select value={filters.minValue} onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}>
          <option value="">Any value</option>
          <option value="10000">$10K+</option>
          <option value="50000">$50K+</option>
          <option value="100000">$100K+</option>
          <option value="500000">$500K+</option>
        </select>
        <select value={filters.sinceDays} onChange={(e) => setFilters({ ...filters, sinceDays: e.target.value })}>
          <option value="">Any date</option>
          <option value="1">Last 24h</option>
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
        </select>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading && <div className="empty">Scanning the radar…</div>}
      {!loading && opps.length === 0 && (
        <div className="empty">
          No opportunities match these filters right now.<br />
          Collectors run automatically — check Source Health if this stays empty.
        </div>
      )}

      <div className="cards">
        {opps.map((opp) => (
          <div className="card" key={opp.id}>
            <div className="score-block">
              <div className={`score-num ${opp.band}`}>{opp.score}</div>
              <span className={`band ${opp.band}`}>{opp.band}</span>
            </div>
            <div className="card-body">
              <div className="card-title">
                {opp.is_demo && '[DEMO] '}
                {opp.title}
              </div>
              <div className="card-meta">
                <span>{opp.location_text ?? 'Location unknown'}</span>
                <span className="money">{fmtValueRange(opp.est_value_low, opp.est_value_high)}</span>
                {opp.response_deadline && <span className="deadline">Bid deadline {fmtDate(opp.response_deadline)}</span>}
                <span>{TYPE_LABELS[opp.opportunity_type] ?? opp.opportunity_type}</span>
                <span>Intent: {opp.intent}</span>
                <span>Urgency: {opp.urgency}</span>
                {opp.corroboration_count > 0 && <span>{opp.corroboration_count} corroborating signal{opp.corroboration_count > 1 ? 's' : ''}</span>}
                <span>Source: {opp.primary_source_id === 'sam_gov' ? 'SAM.gov' : opp.primary_source_id === 'scbo' ? 'SCBO' : opp.primary_source_id}</span>
              </div>
              {opp.explanation && (
                <div className="why"><b>WHY CHASE IT</b>{opp.explanation}</div>
              )}
              <div className="actions">
                <button className={`btn ${opp.status === 'saved' ? 'done' : ''}`} disabled={!!busy[opp.id]}
                  onClick={() => doAction(opp, opp.status === 'saved' ? 'unsave' : 'save', 'Saved')}>
                  {opp.status === 'saved' ? 'SAVED' : 'SAVE'}
                </button>
                <button className="btn" disabled={!!busy[opp.id]} onClick={() => doResearchProperty(opp)}>
                  {busy[opp.id] === 'research_property' ? 'RESEARCHING…' : 'RESEARCH PROPERTY'}
                </button>
                <button className="btn" disabled={!!busy[opp.id]} onClick={() => doResearchAccount(opp)}>
                  {busy[opp.id] === 'research_account' ? 'RESEARCHING…' : 'RESEARCH ACCOUNT'}
                </button>
                <button className="btn" disabled={!!busy[opp.id]} onClick={() => doAction(opp, 'contacted', 'Marked contacted')}>
                  CONTACTED
                </button>
                <button className="btn primary" disabled={!!busy[opp.id]} onClick={() => doCrmPush(opp)}>
                  {busy[opp.id] === 'crm' ? 'PUSHING…' : 'ADD TO CRM'}
                </button>
                <button className="btn quiet" disabled={!!busy[opp.id]} onClick={() => doAction(opp, 'not_for_me', 'Hidden')}>
                  NOT FOR ME
                </button>
                {opp.source_url && (
                  <a className="btn quiet" href={opp.source_url} target="_blank" rel="noreferrer">
                    OFFICIAL SOURCE ↗
                  </a>
                )}
                {flash[opp.id] && <span style={{ color: 'var(--teal)', fontSize: 12, alignSelf: 'center' }}>{flash[opp.id]}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
